import Icon from 'assets/icons';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const KNOWN_ERRORS = {
    "Maximum participant count reached for this invite link.": {
        title: "Maximum Participation Limit Exceeded!",
        description:
            "The maximum participant limit for this training programme has been reached. New registrations are not allowed.",
    },
    'User not found for this program batch': {
        title: 'User Not Found',
        description:
            'Please sign in with the same e-mail ID that received the programme link.',
    },
    'Invalid token': {
        title: 'Invalid Link',
        description: 'The link you used is invalid or has expired.',
    },
    'Couldn’t load programme information.': {
        title: 'Error Loading Program',
        description: 'Unable to load programme information. Please try again later.',
    },
    'Invalid invite token': {
        title: 'Invalid Link',
        description: 'The link or token provided is invalid. Please check and try again.',
    },
    'Your email domain (${emailDomain}) is not allowed for this invite.': {
        title: 'Email Domain Not Allowed',
        description: 'Your email domain (${emailDomain}) is not permitted to access this invite. Please use an approved email address or contact the admin for support.',
    },
    'Please contact the admin or trainer to add your ID to the learner list.': {
        title: 'Learner Access Required',
        description: 'You are not listed as a learner for this program. Please contact the admin or trainer to be added to the learner list.',
    },
    'Unexpected error during invite link validation.': {
        title: 'Invite Link Error',
        description: 'An unexpected error occurred while validating your invite link. Please try again later or contact support for assistance.',
    },
    'Trainee already registered for this program batch.': {
        title: 'Already Registered',
        description: 'You have already registered for this program batch. Please check your dashboard or contact support if you need further assistance.',
    },
    'User email is required': {
        title: 'Email Required',
        description: 'Please enter a valid email address to proceed. The system requires your email to identify and register your participation.'
    },
    'Trainee not found for this email and program batch': {
        title: 'Trainee Not Found',
        description: 'We couldn\'t find a trainee associated with this email and program batch. Please verify the details or contact support for help.'
    },
    'No participant found for this program batch': {
        title: 'Participant Not Found',
        description: 'No registered participants were found for this program batch. Please check the batch details or reach out to your coordinator.'
    },
    'This user is not part of the program batch for this link': {
        title: 'User is not part of the program batch for this link',
        description: 'This user is not part of the program batch for this link'
    }
};
// 🔌 Dummy API response simulation
// const mockApiErrors = ["Session Code is Wrong."];

// function ReenterSessionCode() {
//     return (
//         <div className="bg-[#E9E0FA] mobile:px-5 px-10 py-2 rounded-lg shadow-md mt-4">
//             <form className="mobile:flex-col justify-between mobile:gap-2 flex items-center mobile:space-x-0 space-x-4">
//                 <label htmlFor="sessionNo" className="text-[#5F488A] font-medium">
//                     <strong>Enter Session Code</strong>
//                 </label>
//                 <input
//                     id="sessionNo"
//                     type="text"
//                     className="mobile:w-full w-2/3 border border-gray-300 rounded px-4 py-2 focus:outline-none"
//                     placeholder="e.g., 52343252"
//                 />
//             </form>
//         </div>
//     );
// }

function ErrorPage() {
    const errorImage = "/img/Cancel.png";
    const [errors, setErrors] = useState<string[]>([]);
    const [showReenter, setShowReenter] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const errorState = location.state;
    const email = errorState.email;


    const handleClose = () => {
        navigate('/login');
    };

    const handleContinue = () => {
        navigate('/dashboard');
    };

    useEffect(() => {
        const errorMessage = location.state?.error || location.state?.message || '';
        if (errorMessage && KNOWN_ERRORS[errorMessage]) {
            setErrors([errorMessage]);
        } else {
            setErrors(['Couldn’t load programme information.']);
        }
    }, [errorState]);

    return (
        <div className="relative z-10 bg-[#E9E0FA] bg-opacity-90 rounded-xl mobile:px-[40px] tablet:px-[80px] laptop:px-[100px] px-[150px] pb-[80px] pt-[30px] shadow-lg w-full max-w-[80%]">
            <div className="flex flex-col items-center gap-2 justify-center mb-2">
                <img width={130} src={Icon.secondLogo} alt="Icon Image" />
                <h1 className="mobile:text-xl text-xl text-[#5F488A]">
                    Sorry Learner cannot proceed
                </h1>
            </div>

            <div className="w-full max-w-6xl px-4 mobile:px-2 tablet:px-8 py-6 grid grid-cols-1 gap-6">
                {errors.map((error, index) => {
                    const matchedError = KNOWN_ERRORS[error];

                    if (!matchedError) return null; // Skip unknown errors

                    return (
                        <div
                            key={index}
                            className="flex mobile:flex-col tablet:flex-row bg-[#FFD2D2] rounded-xl shadow-md p-4 gap-4"
                        >
                            <div className="flex justify-center tablet:justify-start">
                                <img
                                    src={errorImage}
                                    alt="Error"
                                    className="w-[60px] h-[60px] mobile:w-[40px] mobile:h-[40px] object-contain"
                                />
                            </div>
                            <div className="text-left mobile:text-center flex flex-col justify-start w-full">
                                <h2 className="text-md mobile:text-sm font-semibold text-app.blue">
                                    {matchedError.title}
                                </h2>
                                <p className="text-sm mobile:text-xs text-gray-700 mt-1">
                                    {matchedError.description}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className='flex gap-10 justify-center items-center'>
                <button onClick={handleClose}
                    className="flex flex-row bg-gradient-to-r shadow text-white text-xl  enabled:from-app-blue enabled:to-app-purple w-1/4 py-2 rounded-full mobile:text-xs desktop:text-lg largescreen:text-xl disabled:bg-gray-500 disabled:cursor-wait"
                    type="submit"
                >
                    <span className="flex mx-auto my-auto">
                        Close
                    </span>
                </button>
                <button onClick={handleContinue}
                    className="flex flex-row bg-gradient-to-r shadow text-white text-xl  enabled:from-app-blue enabled:to-app-purple w-1/4 py-2 rounded-full mobile:text-xs desktop:text-lg largescreen:text-xl disabled:bg-gray-500 disabled:cursor-wait"
                    type="submit"
                >
                    <span className="flex mx-auto my-auto">
                        Continue
                    </span>
                </button>

            </div>
        </div>
    );
}

export default ErrorPage;
